import { supabase } from './supabase';

export interface SendOtpResult {
  ok: boolean;
  /** false when the email already belongs to a registered account. */
  available?: boolean;
  /** 'team_member' when the email belongs to a team member (resource) account. */
  reason?: string;
  error?: string;
  retryAfter?: number;
}

export interface VerifyOtpResult {
  ok: boolean;
  error?: string;
}

/** Send a 6-digit verification code to the email (also checks it isn't already in use). */
export async function sendSignupOtp(email: string): Promise<SendOtpResult> {
  if (!supabase) return { ok: false, error: 'no_client' };
  const { data, error } = await supabase.functions.invoke('send-signup-otp', {
    body: { email: email.trim().toLowerCase() },
  });
  if (error) {
    // Edge function returned a non-2xx → try to surface its JSON body.
    const ctx = (error as any)?.context;
    if (ctx && typeof ctx.json === 'function') {
      try { return (await ctx.json()) as SendOtpResult; } catch { /* ignore */ }
    }
    return { ok: false, error: 'request_failed' };
  }
  return (data ?? { ok: false, error: 'no_response' }) as SendOtpResult;
}

/**
 * Same availability rules as sendSignupOtp (users / resources / auth.users)
 * without sending a verification email. Used by signup-nexus before password.
 */
export async function checkSignupEmailAvailable(email: string): Promise<SendOtpResult> {
  if (!supabase) return { ok: false, error: 'no_client' };
  const { data, error } = await supabase.functions.invoke('send-signup-otp', {
    body: { email: email.trim().toLowerCase(), checkOnly: true },
  });
  if (error) {
    const ctx = (error as any)?.context;
    if (ctx && typeof ctx.json === 'function') {
      try { return (await ctx.json()) as SendOtpResult; } catch { /* ignore */ }
    }
    return { ok: false, error: 'request_failed' };
  }
  return (data ?? { ok: false, error: 'no_response' }) as SendOtpResult;
}

/** Shared copy for duplicate / team-member email blocks. */
export function signupEmailUnavailableMessage(reason?: string): string {
  if (reason === 'team_member') {
    return 'This email belongs to a team member account. Log in instead, or use a different email to create a new business.';
  }
  return 'An account with this email already exists — log in instead.';
}

/** Verify the 6-digit code for an email. */
export async function verifySignupOtp(email: string, code: string): Promise<VerifyOtpResult> {
  if (!supabase) return { ok: false, error: 'no_client' };
  const { data, error } = await supabase.functions.invoke('verify-signup-otp', {
    body: { email: email.trim().toLowerCase(), code: code.trim() },
  });
  if (error) {
    const ctx = (error as any)?.context;
    if (ctx && typeof ctx.json === 'function') {
      try { return (await ctx.json()) as VerifyOtpResult; } catch { /* ignore */ }
    }
    return { ok: false, error: 'request_failed' };
  }
  return (data ?? { ok: false, error: 'no_response' }) as VerifyOtpResult;
}
